import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { invalidateBoard, syncCardInBoard } from '@/entities/board';
import type { CardDetail } from '@/entities/card';
import { api, queryKeys } from '@/shared/api';
import type { JournalQuery } from '@/shared/lib/journal-search';

import type { ActivityEvent, Page, VersionDetail, VersionSummary } from './types';

const PAGE = 30;

/** A cursor-paginated list, newest first; `fetchNextPage` loads the older ones. */
function usePaged<T>(
  key: readonly unknown[],
  path: string,
  query: Record<string, string> = {},
  repeat?: Record<string, readonly string[]>,
) {
  return useInfiniteQuery({
    // Longer than the registry key on purpose: invalidating the registry key (a prefix) still hits it.
    queryKey: [...key, path, query, repeat ?? null],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<T>>(path, {
        signal,
        query: { ...query, limit: PAGE, cursor: pageParam ?? undefined },
        ...(repeat && { repeat }),
      }),
    placeholderData: keepPreviousData,
    getNextPageParam: (last) => last.next_cursor,
  });
}

/** The card's own events; a search narrows the card's WHOLE history on the server. */
export function useCardHistory(cardId: string, search: JournalQuery = {}) {
  return usePaged<ActivityEvent>(
    queryKeys.cards.history(cardId),
    `/v1/cards/${cardId}/history`,
    search.q ? { q: search.q } : {},
    search.types?.length ? { types: search.types } : undefined,
  );
}

export function useCardVersions(cardId: string) {
  return usePaged<VersionSummary>(queryKeys.cards.versions(cardId), `/v1/cards/${cardId}/versions`);
}

/** The board's feed; fetched only while its panel is open. */
export function useBoardActivity(
  boardId: string,
  entityType: ActivityEvent['entity_type'] | null,
  search: JournalQuery = {},
) {
  return usePaged<ActivityEvent>(
    queryKeys.activity.board(boardId),
    `/v1/boards/${boardId}/activity`,
    { ...(entityType && { entity_type: entityType }), ...(search.q && { q: search.q }) },
    search.types?.length ? { types: search.types } : undefined,
  );
}

export function useCardVersion(cardId: string, versionId: string | null) {
  return useQuery({
    queryKey: queryKeys.cards.version(cardId, versionId ?? ''),
    queryFn: ({ signal }) =>
      api.get<VersionDetail>(`/v1/cards/${cardId}/versions/${versionId ?? ''}`, { signal }),
    enabled: versionId !== null,
  });
}

/**
 * Restore a version as a NEW change. The card in the cache (and its board preview) is replaced
 * by the answer; history lists are refetched because the restore added an event and a version.
 */
export function useRestoreVersion(cardId: string, boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, version }: { versionId: string; version: number }) =>
      api.post<CardDetail>(`/v1/cards/${cardId}/versions/${versionId}/restore`, {
        expected_version: version,
      }),
    onSuccess: (card) => {
      client.setQueryData<CardDetail>(queryKeys.cards.detail(cardId), card);
      syncCardInBoard(client, card);
      void client.invalidateQueries({ queryKey: queryKeys.cards.history(cardId) });
      void client.invalidateQueries({ queryKey: queryKeys.cards.versions(cardId) });
      void client.invalidateQueries({ queryKey: queryKeys.activity.board(boardId) });
      void invalidateBoard(client, boardId);
    },
    onError: () => client.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) }),
  });
}
